// `wl_reorder_subscription` A/B/C test. The API decides eligibility; the arm is assigned here per
// device (localStorage). A = control, B = My Account CTA + Care+ offer, C = Care+ offer only.

import { ABTestManager } from "./ABTestManager";
import { MainTracker } from "../PixelTrackers/MainTracker";

export type WlReorderVariant = "A" | "B" | "C";
/** The variable's JSON, e.g. {"A":0.34,"B":0.33,"C":0.33}. */
export type WlReorderWeights = Partial<Record<WlReorderVariant, number>>;

export const WL_REORDER_TEST_NAME = "wl_reorder_subscription";
export const WL_REORDER_VARIANTS: readonly WlReorderVariant[] = ["A", "B", "C"];
export const WL_REORDER_TRACKING_PROPERTY = "WL Test";
/** `?intent=subscription`: came from the B CTA. */
export const WL_REORDER_INTENT_PARAM = "intent";
export const WL_REORDER_SUBSCRIPTION_INTENT = "subscription";
/** Fired on My Orders (HL-12095). */
export const WL_REORDER_TEST_EVENT = "Funnel Reorder WL Test";

const STORAGE_KEY = `abtest_${WL_REORDER_TEST_NAME}`;
const CONTROL_WEIGHTS = [1, 0, 0];

const isVariant = (value: string | null): value is WlReorderVariant =>
  value !== null && (WL_REORDER_VARIANTS as readonly string[]).includes(value);

const parseWeightsJson = (raw: string): WlReorderWeights | null => {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** Normalised [A, B, C] weights; anything invalid = all A. */
export const parseWlReorderWeights = (raw: string): number[] => {
  const weightsByArm = parseWeightsJson(raw);
  if (!weightsByArm) return CONTROL_WEIGHTS;
  const weights = WL_REORDER_VARIANTS.map((arm) => {
    // Number(): the JSON is edited by hand, so a value may be a string or garbage.
    const weight = Number(weightsByArm[arm]);
    return Number.isFinite(weight) && weight > 0 ? weight : 0;
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  return total > 0 ? weights.map((weight) => weight / total) : CONTROL_WEIGHTS;
};

const readStoredVariant = (): WlReorderVariant | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isVariant(stored) ? stored : null;
  } catch {
    return null;
  }
};

/**
 * Sticky arm + `WL Test` super property. An arm at weight 0 is re-bucketed (kill switch).
 * My Orders is the main assignment point; Order Details and ReorderLayout only bucket devices that arrive without an arm.
 */
export const assignWlReorderVariant = (rawWeights: string): WlReorderVariant => {
  const weights = parseWlReorderWeights(rawWeights);
  const manager = new ABTestManager({
    testName: WL_REORDER_TEST_NAME,
    variants: [...WL_REORDER_VARIANTS],
    weights,
  });
  if (weights[WL_REORDER_VARIANTS.indexOf(manager.getVariant() as WlReorderVariant)] === 0) {
    manager.resetVariant();
  }
  const raw = manager.getVariant();
  const variant: WlReorderVariant = isVariant(raw) ? raw : "A";
  MainTracker.superProperties({ [WL_REORDER_TRACKING_PROPERTY]: variant });
  return variant;
};

/** Arm for an order: assigned (sticky) when the order is in the test, null otherwise. */
export const assignWlReorderVariantForOffer = (offer: { testWeights: string } | null | undefined): WlReorderVariant | null =>
  offer ? assignWlReorderVariant(offer.testWeights) : null;

/** Sticky read; null when this device was never bucketed. */
export const getWlReorderVariant = (): WlReorderVariant | null => readStoredVariant();

/** `WL Test` event property; null outside the test. */
export const getWlReorderTrackingProps = (variant: WlReorderVariant | null | undefined) => ({
  [WL_REORDER_TRACKING_PROPERTY]: variant ?? null,
});

/** `test` value of the My Orders event: lowercase, per the ticket. */
export const toWlReorderTestEventValue = (variant: WlReorderVariant): string => variant.toLowerCase();

/** B only. */
export const showsSwitchToSubscriptionCta = (variant: WlReorderVariant | null | undefined): boolean => variant === "B";

/** B and C. */
export const offersSubscriptionOnReorderScreen = (variant: WlReorderVariant | null | undefined): boolean =>
  variant === "B" || variant === "C";

export const isSubscriptionIntent = (searchParams: URLSearchParams): boolean =>
  searchParams.get(WL_REORDER_INTENT_PARAM) === WL_REORDER_SUBSCRIPTION_INTENT;
